import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useConvexAuth } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import useAppStore from '../../stores/appStore';
import SignOutButton from '../Auth/SignOutButton';
import { isValidProjectId, normalizeProjectId } from '../../utils/projectIds';
import { ACHIEVEMENTS } from '../../../shared/achievements';
import { CREDIT_PAYMENT_URL } from '../../../shared/billing';
import {
  formatMonthShort,
  formatRelativeTimeCompact,
  getAchievementTitle,
  getTranslatedGenre,
  getTranslatedProjectStatus,
} from '../../utils/i18nHelpers';
import './ProjectHub.css';

function GoalRing({ current, goal }) {
  const { t } = useTranslation();
  const pct = Math.min(current / Math.max(goal, 1), 1);
  const radius = 54;
  const stroke = 7;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - pct);

  return (
    <svg className="goal-ring" viewBox="0 0 128 128" width="128" height="128">
      <circle cx="64" cy="64" r={radius} fill="none" stroke="rgba(212,162,106,0.1)" strokeWidth={stroke} />
      <circle
        className="goal-ring-fill"
        cx="64"
        cy="64"
        r={radius}
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        transform="rotate(-90 64 64)"
      />
      <text x="64" y="56" textAnchor="middle" className="goal-ring-count">
        {current}
      </text>
      <text x="64" y="74" textAnchor="middle" className="goal-ring-label">
        {t('projects.goalWords', { goal })}
      </text>
    </svg>
  );
}

function HeatmapGrid({ heatmap }) {
  const { t, i18n } = useTranslation();
  const safeHeatmap = Array.isArray(heatmap) ? heatmap : [];
  if (safeHeatmap.length === 0) {
    return <div className="streak-heatmap-grid" />;
  }

  const firstDay = new Date(`${safeHeatmap[0].date}T00:00:00Z`).getUTCDay();
  const padded = [...Array(firstDay).fill(null), ...safeHeatmap];
  while (padded.length % 7 !== 0) {
    padded.push(null);
  }

  const columns = Math.max(1, Math.ceil(padded.length / 7));
  const monthMarkers = [];
  let lastMonth = '';

  for (let col = 0; col < columns; col += 1) {
    const cell = padded[col * 7];
    if (!cell) continue;
    const date = new Date(`${cell.date}T00:00:00Z`);
    const monthKey = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
    if (monthKey !== lastMonth) {
      monthMarkers.push({
        key: monthKey,
        col,
        label: formatMonthShort(i18n, date),
      });
      lastMonth = monthKey;
    }
  }

  return (
    <div className="streak-heatmap-wrapper">
      <div className="streak-heatmap-months" style={{ gridTemplateColumns: `repeat(${columns}, 12px)` }}>
        {monthMarkers.map((marker) => (
          <span key={marker.key} className="streak-month-label" style={{ gridColumn: `${marker.col + 1}` }}>
            {marker.label}
          </span>
        ))}
      </div>

      <div className="streak-heatmap-grid" style={{ gridTemplateColumns: `repeat(${columns}, 12px)` }}>
        {padded.map((day, index) => {
          if (!day) {
            return <span key={`empty-${index}`} className="streak-cell empty" />;
          }
          return (
            <span
              key={day.date}
              className={`streak-cell level-${day.level}`}
              title={`${day.date} • ${day.words} ${t('common.words')}`}
            />
          );
        })}
      </div>

      <div className="streak-heatmap-legend">
        <span>{t('common.less')}</span>
        <span className="streak-cell level-0" />
        <span className="streak-cell level-1" />
        <span className="streak-cell level-2" />
        <span className="streak-cell level-3" />
        <span className="streak-cell level-4" />
        <span>{t('common.more')}</span>
      </div>
    </div>
  );
}

function HeatmapWidget({ heatmap }) {
  return (
    <div className="heatmap-widget">
      <HeatmapGrid heatmap={heatmap} />
    </div>
  );
}

function StreakWidget({ streak, weeklyActivity }) {
  const { t } = useTranslation();
  const activeDays = (weeklyActivity || []).filter(Boolean).length;

  return (
    <div className="streak-widget">
      <div className="streak-flame-row">
        <span className="streak-flame">🔥</span>
        <span className="streak-count">{streak}</span>
        <span className="streak-label">{t('projects.dayStreak')}</span>
      </div>
      <div className="streak-subtext">{t('projects.activeDaysThisWeek', { count: activeDays })}</div>
    </div>
  );
}

function NextAchievementTeaser() {
  const { t } = useTranslation();
  const progress = useQuery(api.achievements.getProgress);
  const userAchievements = useQuery(api.achievements.listForUser);

  if (!progress || !userAchievements) return null;

  const unlockedSet = new Set(userAchievements.map((a) => a.achievementId));

  const getVal = (a) => {
    if (a.category === 'volume') return progress.totalUserWords;
    if (a.category === 'streaks') return progress.currentStreak;
    if (a.id === 'first-project' || a.id === 'three-projects') return progress.projectCount;
    if (a.id === 'first-chapter') return progress.chapterCount;
    if (a.id === 'first-review') return progress.completedReviews;
    if (a.id === 'first-audience') return progress.completedAudienceTests;
    if (a.id === 'first-bible') return progress.storyBibleCount;
    if (a.id === 'first-post') return progress.postCount;
    if (a.id === 'first-comment-received') return progress.commentsReceived;
    if (a.id === 'ten-followers' || a.id === 'fifty-followers') return progress.followerCount;
    return 0;
  };

  const next = ACHIEVEMENTS.find((a) => !unlockedSet.has(a.id));
  if (!next) return null;

  const threshold = next.threshold ?? 1;
  const current = getVal(next);
  const remaining = Math.max(threshold - current, 0);
  const pct = Math.min(current / threshold, 1);

  return (
    <a href="/projects/rewards" className="hub-next-ach" style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="hub-next-ach__top">
        <span className="hub-next-ach__icon">{next.icon}</span>
        <span className="hub-next-ach__title">{t('projects.nextAchievement', { title: getAchievementTitle(t, next.id, next.title) })}</span>
        <span className="hub-next-ach__credits">+{next.credits}</span>
      </div>
      <div className="hub-next-ach__bar">
        <div className="hub-next-ach__fill" style={{ width: `${pct * 100}%` }} />
      </div>
      <span className="hub-next-ach__sub">
        {t('projects.achievementToGo', { count: remaining.toLocaleString(), unit: next.category === 'volume' ? t('common.words') : t('common.more') })}
      </span>
    </a>
  );
}

const genreColors = {
  Fiction: 'var(--color-fiction)',
  'Non-Fiction': 'var(--color-nonfiction)',
  'Sci-Fi': 'var(--color-scifi)',
  Mystery: 'var(--color-mystery)',
  Romance: 'var(--color-romance)',
  Fantasy: 'var(--color-fantasy)',
  Biography: 'var(--color-biography)',
  'Self-Help': 'var(--color-selfhelp)',
};

function CardMenu({
  projectId,
  projectTitle,
  isPinned,
  onOpenProject,
  onActionError,
  onActionMessage,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(projectTitle);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const updateMetadata = useMutation(api.projects.updateMetadata);
  const archiveProject = useMutation(api.projects.archiveProject);
  const togglePinned = useMutation(api.projects.togglePinned);

  useEffect(() => {
    if (!open && !renaming) return;
    const handler = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
        setRenaming(false);
        setConfirmingArchive(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, renaming]);

  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);

  const stopAnd = (event, fn) => {
    event.stopPropagation();
    fn();
  };

  const handleRenameSubmit = async (event) => {
    event?.stopPropagation?.();
    const trimmed = newTitle.trim();
    setRenaming(false);

    if (!trimmed || trimmed === projectTitle) return;

    try {
      setIsBusy(true);
      await updateMetadata({ projectId, title: trimmed });
      onActionMessage?.(t('projects.renamedProject', { title: projectTitle }));
    } catch (error) {
      onActionError?.(error.message || t('projects.failedToRename'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleTogglePin = async (event) => {
    event.stopPropagation();
    try {
      setIsBusy(true);
      const result = await togglePinned({ projectId });
      onActionMessage?.(result.pinned ? t('projects.projectPinned') : t('projects.projectUnpinned'));
      setOpen(false);
    } catch (error) {
      onActionError?.(error.message || t('projects.failedToPin'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleArchive = async (event) => {
    event.stopPropagation();
    if (!confirmingArchive) {
      setConfirmingArchive(true);
      return;
    }

    try {
      setIsBusy(true);
      await archiveProject({ projectId });
      onActionMessage?.(t('projects.deletedProject', { title: projectTitle }));
      setOpen(false);
      setConfirmingArchive(false);
    } catch (error) {
      onActionError?.(error.message || t('projects.failedToArchive'));
    } finally {
      setIsBusy(false);
    }
  };

  if (renaming) {
    return (
      <div className="hub-card-rename" ref={menuRef} onClick={(event) => event.stopPropagation()}>
        <input
          ref={inputRef}
          className="hub-card-rename-input"
          value={newTitle}
          onChange={(event) => setNewTitle(event.target.value)}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === 'Enter') {
              handleRenameSubmit(event);
            }
            if (event.key === 'Escape') {
              setRenaming(false);
            }
          }}
          onBlur={handleRenameSubmit}
        />
      </div>
    );
  }

  return (
    <div className="hub-card-menu-wrapper" ref={menuRef}>
      <button
        className="hub-card-dots"
        onClick={(event) => stopAnd(event, () => {
          setOpen((prev) => !prev);
          setConfirmingArchive(false);
        })}
        title={t('projects.moreOptions')}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>
      {open && (
        <div className="hub-card-menu">
          <button
            className="hub-card-menu-item"
            onClick={(event) => stopAnd(event, () => {
              setOpen(false);
              setConfirmingArchive(false);
              setNewTitle(projectTitle);
              setRenaming(true);
            })}
          >
            ✏️ {t('projects.renameProject')}
          </button>
          <button className="hub-card-menu-item" onClick={handleTogglePin} disabled={isBusy}>
            {isPinned ? `📍 ${t('projects.unpinProject')}` : `📌 ${t('projects.pinToTop')}`}
          </button>
          <button
            className={`hub-card-menu-item ${confirmingArchive ? 'danger' : ''}`}
            onClick={handleArchive}
            disabled={isBusy}
          >
            {confirmingArchive ? `⚠️ ${t('projects.confirmDelete')}` : `🗑️ ${t('projects.deleteProject')}`}
          </button>
        </div>
      )}
    </div>
  );
}

function ProjectHub({ projects, onOpenProject, onCreateProject, onOpenCommunity, onOpenChallenges, onOpenChallengeAdmin, onResetWorkspace }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setCurrentProjectId = useAppStore((state) => state.setCurrentProjectId);
  const dailyGoal = useAppStore((state) => state.dailyGoal);

  const ensureCredits = useMutation(api.credits.ensureForUser);
  const createExampleProject = useMutation(api.projects.createExampleProject);
  const creditStatus = useQuery(api.credits.getStatus);
  const writingInsights = useQuery(api.projects.getWritingInsights, { days: 140 });
  const { isAuthenticated } = useConvexAuth();

  const didInitRef = useRef(false);
  const [isCreatingExample, setIsCreatingExample] = useState(false);
  const [exampleMessage, setExampleMessage] = useState('');
  const [exampleError, setExampleError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    setCurrentProjectId(null);
    onResetWorkspace?.();
  }, [setCurrentProjectId, onResetWorkspace]);

  useEffect(() => {
    if (!isAuthenticated) return;
    ensureCredits();
  }, [ensureCredits, isAuthenticated]);

  const handleOpenProject = useCallback(
    (rawProjectId) => {
      const projectId = normalizeProjectId(rawProjectId);
      if (!isValidProjectId(projectId)) {
        setActionError(t('common.invalidProjectLink'));
        return;
      }
      onOpenProject?.(projectId);
    },
    [onOpenProject],
  );

  if (!Array.isArray(projects)) {
    return (
      <div className="project-hub">
        <div className="hub-empty-state">
          <h2>{t('projects.couldNotLoadProjects')}</h2>
          <p>{t('projects.refreshToTry')}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            {t('common.reload')}
          </button>
        </div>
      </div>
    );
  }

  const totalWords = projects.reduce((sum, project) => sum + (project.wordCount || 0), 0);
  const totalCredits = creditStatus?.totalRemaining ?? 0;

  const todayWords = writingInsights?.todayWords ?? 0;
  const streak = writingInsights?.currentStreak ?? 0;
  const weeklyActivity = writingInsights?.weeklyActivity ?? [false, false, false, false, false, false, false];
  const heatmap = writingInsights?.heatmap ?? [];

  const handleCreateExampleProject = async () => {
    if (isCreatingExample) return;
    setExampleError('');
    setExampleMessage('');
    setIsCreatingExample(true);

    try {
      const result = await createExampleProject({});
      setExampleMessage(t('projects.sampleCreatedWithChapters', { count: result.chapterCount }));
      handleOpenProject(result.projectId);
    } catch (error) {
      setExampleError(error.message || t('common.failedToCreateSample'));
    } finally {
      setIsCreatingExample(false);
    }
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('projects.greetingMorning') : hour < 17 ? t('projects.greetingAfternoon') : t('projects.greetingEvening');

  return (
    <div className="project-hub">
      <header className="hub-topbar">
        <span className="hub-logo">✒️ {t('common.appName')}</span>
        <div className="hub-topbar-actions">
          <button
            className="hub-credit-pill"
            onClick={() => window.location.assign(CREDIT_PAYMENT_URL)}
            title={t('projects.viewCredits')}
          >
            🪙 {Number(totalCredits).toFixed(1)}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/profile')}
          >
            {t('profile.editProfile')}
          </button>
          <SignOutButton />
        </div>
      </header>

      <section className="hub-dashboard">
        <div className="hub-greeting">
          <h1>{t('projects.greetingWriter', { greeting })}</h1>
          <p>
            {totalWords > 0
              ? t('projects.wordsAcrossProjects', { count: totalWords.toLocaleString(), projectCount: projects.length, plural: projects.length !== 1 ? 's' : '' })
              : t('projects.startFirstProject')}
          </p>
        </div>

        <div className="hub-widgets">
          <div className="hub-widget-card hub-widget-card-heatmap">
            <div className="hub-widget-label">{t('projects.heatmap')}</div>
            <HeatmapWidget heatmap={heatmap} />
          </div>
          <div className="hub-widget-card hub-widget-card-metric">
            <div className="hub-widget-label">{t('projects.todaysGoal')}</div>
            <GoalRing current={todayWords} goal={dailyGoal} />
            <div className="hub-streak-inline">
              <span className="hub-streak-inline__flame">🔥</span>
              <span className="hub-streak-inline__count">{streak}</span>
              <span className="hub-streak-inline__label">{t('projects.dayStreak')}</span>
            </div>
          </div>
        </div>
        <NextAchievementTeaser />
      </section>

      {exampleError && <div className="hub-alert hub-alert-error">{exampleError}</div>}
      {actionError && <div className="hub-alert hub-alert-error">{actionError}</div>}
      {exampleMessage && <div className="hub-alert hub-alert-success">{exampleMessage}</div>}
      {actionMessage && <div className="hub-alert hub-alert-success">{actionMessage}</div>}

      <section className="hub-projects-section">
        <div className="hub-projects-header">
          <h2>{t('projects.yourProjects')}</h2>
          <div className="hub-projects-actions">
            <button className="btn btn-ghost" onClick={onOpenCommunity}>
              {t('common.community')}
            </button>
            <button className="btn btn-ghost" onClick={onOpenChallenges}>
              {t('common.challenges')}
            </button>
            <button className="btn btn-ghost" onClick={onOpenChallengeAdmin}>
              {t('common.challengeAdmin', { defaultValue: 'Challenge Admin' })}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleCreateExampleProject}
              disabled={isCreatingExample}
            >
              {isCreatingExample ? t('projects.creatingExample') : t('projects.sampleProject')}
            </button>
            <button className="btn btn-primary" onClick={onCreateProject}>
              {t('projects.newProject')}
            </button>
          </div>
        </div>

        {projects.length === 0 ? (
          <div className="hub-empty-state">
            <span className="hub-empty-icon">📝</span>
            <h2>{t('projects.studioEmpty')}</h2>
            <p>{t('projects.studioEmptyDesc')}</p>
            <div className="hub-empty-actions">
              <button
                className="btn btn-secondary"
                onClick={handleCreateExampleProject}
                disabled={isCreatingExample}
              >
                {isCreatingExample ? t('projects.creatingExample') : t('projects.sampleProjectLower')}
              </button>
              <button className="btn btn-primary" onClick={onCreateProject}>
                {t('projects.createFirstProject')}
              </button>
            </div>
          </div>
        ) : (
          <div className="hub-project-grid">
            {projects.map((project) => {
              const accentColor = genreColors[project.genre] || 'var(--color-accent)';
              return (
                <div
                  key={project._id}
                  className="hub-project-card"
                  onClick={() => handleOpenProject(project._id)}
                >
                  <div className="hub-card-accent" style={{ background: accentColor }} />
                  <div className="hub-card-body">
                    <div className="hub-card-top">
                      <span className="hub-card-title">{project.title}</span>
                      <div className="hub-card-top-right">
                        {project.pinnedAt ? <span className="hub-card-pinned">📌</span> : null}
                        <span className="hub-card-status">{getTranslatedProjectStatus(t, project.status || 'drafting')}</span>
                        <CardMenu
                          projectId={project._id}
                          projectTitle={project.title}
                          isPinned={Boolean(project.pinnedAt)}
                          onOpenProject={handleOpenProject}
                          onActionMessage={(message) => {
                            setActionError('');
                            setActionMessage(message);
                          }}
                          onActionError={(message) => {
                            setActionMessage('');
                            setActionError(message);
                          }}
                        />
                      </div>
                    </div>
                    {project.synopsis ? <p className="hub-card-synopsis">{project.synopsis}</p> : null}
                    <div className="hub-card-footer">
                      <div className="hub-card-meta">
                        {project.genre ? <span>{getTranslatedGenre(t, project.genre)}</span> : null}
                        <span>{(project.wordCount || 0).toLocaleString()} {t('common.words')}</span>
                        {project.updatedAt ? <span>{formatRelativeTimeCompact(t, project.updatedAt)}</span> : null}
                      </div>
                      <span className="hub-card-arrow">→</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default ProjectHub;
